import {
  ExternalLink,
  ListMusic,
  LoaderCircle,
  Pause,
  Play,
  RotateCw,
  Shuffle,
  SkipBack,
  SkipForward,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '../design-system/index.js';
import { QuickCreateButton } from '../features/create/quick-create.js';
import { CurrentTrackFavoriteButton } from '../features/tracks/current-track-favorite.js';
import { CurrentTrackRemovalButton } from '../features/tracks/current-track-removal.js';
import { formatPlaybackTime } from './format-time.js';
import { usePlayer } from './player-context.js';

const mobileTransportControlClass = 'size-14 [&_svg]:size-6';

function PlayIcon() {
  const player = usePlayer();
  if (player.status === 'loading' || player.status === 'waiting') {
    return <LoaderCircle className="animate-spin motion-reduce:animate-none" aria-hidden />;
  }
  if (player.status === 'playing') return <Pause fill="currentColor" aria-hidden />;
  return <Play fill="currentColor" aria-hidden />;
}

function PlaybackScrubber() {
  const player = usePlayer();
  const duration = Math.max(player.duration, 0);

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="w-9 shrink-0 text-right font-mono text-xs tabular-nums text-ink-3">
        {formatPlaybackTime(player.currentTime)}
      </span>
      <input
        type="range"
        min={0}
        max={duration}
        step={0.1}
        value={Math.min(player.currentTime, duration)}
        onChange={(event) => player.seek(event.currentTarget.valueAsNumber)}
        disabled={!duration}
        aria-label="Playback position"
        className="h-11 min-w-0 flex-1 cursor-pointer accent-accent disabled:cursor-not-allowed disabled:opacity-45"
      />
      <span className="w-9 shrink-0 font-mono text-xs tabular-nums text-ink-3">
        {formatPlaybackTime(player.duration)}
      </span>
    </div>
  );
}

function MobilePlayerArtwork() {
  return (
    <div
      aria-hidden="true"
      data-player-artwork="mobile"
      className="size-11 shrink-0 rounded-md border border-border bg-[linear-gradient(135deg,var(--surface-hi),var(--bg-2))]"
    />
  );
}

function MobileNowPlaying() {
  const player = usePlayer();
  if (!player.item) return null;

  const needsRefresh = player.status === 'source-expired';
  const hasQueue = player.queue.length > 0 && player.queueIndex !== null;

  return (
    <section
      className="bg-bg-2 py-3 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]"
      aria-label="Now playing"
    >
      <div className="flex min-w-0 items-center gap-3">
        <MobilePlayerArtwork />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink">{player.item.title}</p>
          <p className="truncate text-xs text-ink-3">
            {[player.item.artist, player.playbackContext?.label].filter(Boolean).join(' · ') ||
              player.source?.label ||
              'Preparing source'}
          </p>
        </div>
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="grid size-11 shrink-0 place-items-center rounded-md text-ink-2 outline-none hover:bg-surface-hi hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring"
        >
          <Link to="/now-playing" aria-label="Open full player">
            <ExternalLink className="size-4" aria-hidden />
          </Link>
        </Button>
      </div>

      <div className="mt-1">
        <PlaybackScrubber />
      </div>

      {player.error ? (
        <p className="mt-2 text-xs text-err" role="status">
          {player.error}
        </p>
      ) : null}

      <div
        data-player-transport="mobile"
        className="mt-1 grid grid-cols-[1fr_auto_1fr] items-center"
      >
        {hasQueue ? (
          <Button
            variant="ghost"
            size="icon"
            className={`${mobileTransportControlClass} justify-self-end`}
            onClick={() => void player.previous()}
            disabled={player.queueIndex === 0 && player.currentTime <= 5}
            aria-label="Previous track"
          >
            <SkipBack aria-hidden />
          </Button>
        ) : (
          <span aria-hidden="true" />
        )}

        <Button
          variant="secondary"
          size="icon"
          onClick={() => void (needsRefresh ? player.retry() : player.toggle())}
          className={`${mobileTransportControlClass} grid place-items-center rounded-full border border-border-hi bg-surface-hi text-accent outline-none hover:border-accent-line focus-visible:ring-2 focus-visible:ring-accent-ring`}
          aria-label={
            needsRefresh
              ? 'Refresh playback source'
              : player.status === 'playing'
                ? 'Pause'
                : 'Play'
          }
        >
          {needsRefresh ? <RotateCw aria-hidden /> : <PlayIcon />}
        </Button>

        {hasQueue ? (
          <Button
            variant="ghost"
            size="icon"
            className={`${mobileTransportControlClass} justify-self-start`}
            onClick={() => void player.next()}
            disabled={player.queueIndex === player.queue.length - 1}
            aria-label="Next track"
          >
            <SkipForward aria-hidden />
          </Button>
        ) : (
          <span aria-hidden="true" />
        )}
      </div>

      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
          {hasQueue ? (
            <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap font-mono text-xs text-ink-3">
              <ListMusic className="size-3.5" aria-hidden />
              {player.queueIndex! + 1} / {player.queue.length}
            </span>
          ) : null}
          {hasQueue ? (
            <Button
              variant="ghost"
              size="icon"
              className="size-11"
              onClick={player.toggleShuffle}
              disabled={player.canonicalQueue.length < 2}
              aria-label={player.shuffle ? 'Turn shuffle off' : 'Turn shuffle on'}
              aria-pressed={player.shuffle}
            >
              <Shuffle aria-hidden className={player.shuffle ? 'text-accent' : undefined} />
            </Button>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <CurrentTrackFavoriteButton className="size-11" />
          <QuickCreateButton className="size-11" iconClassName="size-5" />
          <CurrentTrackRemovalButton className="size-11" />
        </div>
      </div>
    </section>
  );
}

export function CompactNowPlaying({ layout }: { layout: 'bar' | 'rail' }) {
  const player = usePlayer();
  if (!player.item) return null;
  if (layout === 'bar') return <MobileNowPlaying />;

  const needsRefresh = player.status === 'source-expired';
  const hasQueue = player.queue.length > 0 && player.queueIndex !== null;

  return (
    <section className="rounded-lg border border-border bg-surface p-3" aria-label="Now playing">
      <div className="flex min-w-0 items-center gap-2">
        <Button
          variant="secondary"
          size="icon"
          onClick={() => void (needsRefresh ? player.retry() : player.toggle())}
          className="grid size-11 shrink-0 place-items-center rounded-full border border-border-hi bg-surface-hi text-accent outline-none hover:border-accent-line focus-visible:ring-2 focus-visible:ring-accent-ring"
          aria-label={
            needsRefresh
              ? 'Refresh playback source'
              : player.status === 'playing'
                ? 'Pause'
                : 'Play'
          }
        >
          {needsRefresh ? <RotateCw aria-hidden /> : <PlayIcon />}
        </Button>
        <Link
          to="/now-playing"
          aria-label={`Open full player for ${player.item.title}`}
          className="group flex min-h-11 min-w-0 flex-1 items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-ink group-hover:text-accent">
                {player.item.title}
              </span>
              <span className="block truncate text-xs text-ink-3">
                {[player.item.artist, player.playbackContext?.label].filter(Boolean).join(' · ') ||
                  player.source?.label ||
                  'Preparing source'}
              </span>
            </span>
            <ExternalLink className="size-3.5 shrink-0 text-ink-3" aria-hidden />
          </span>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={player.close}
          className="grid size-11 shrink-0 place-items-center rounded-md text-ink-2 outline-none hover:bg-surface-hi hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring"
          aria-label="Close player"
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
      <div className="mt-1 border-t border-border pt-1">
        <PlaybackScrubber />
      </div>
      {player.error ? (
        <p className="mt-2 text-xs text-err" role="status">
          {player.error}
        </p>
      ) : null}
      <div className="mt-2 space-y-1.5 border-t border-border pt-2">
        {hasQueue ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap font-mono text-xs text-ink-3">
            <ListMusic className="size-3.5" aria-hidden />
            {player.queueIndex! + 1} / {player.queue.length}
          </span>
        ) : null}
        <div className="flex flex-wrap items-center justify-end gap-1">
          <QuickCreateButton className="size-11" iconClassName="size-4" />
          <CurrentTrackFavoriteButton className="size-11" />
          {hasQueue ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="size-11"
                onClick={player.toggleShuffle}
                disabled={player.canonicalQueue.length < 2}
                aria-label={player.shuffle ? 'Turn shuffle off' : 'Turn shuffle on'}
                aria-pressed={player.shuffle}
              >
                <Shuffle aria-hidden className={player.shuffle ? 'text-accent' : undefined} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-11"
                onClick={() => void player.previous()}
                disabled={player.queueIndex === 0 && player.currentTime <= 5}
                aria-label="Previous track"
              >
                <SkipBack aria-hidden />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-11"
                onClick={() => void player.next()}
                disabled={player.queueIndex === player.queue.length - 1}
                aria-label="Next track"
              >
                <SkipForward aria-hidden />
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export function MobilePlayerHeaderContent() {
  const player = usePlayer();
  if (player.item) return <CompactNowPlaying layout="bar" />;

  return (
    <section
      aria-label="Player shortcuts"
      className="flex min-h-16 items-center justify-center bg-bg-2 py-2 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]"
    >
      <QuickCreateButton
        className="size-11 rounded-full border border-border-hi bg-surface-hi text-accent hover:border-accent-line hover:bg-surface"
        iconClassName="size-5"
      />
    </section>
  );
}
